import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, GripVertical, Loader2 } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { AdminShell } from "@/components/admin/AdminShell";
import { useProducts } from "@/context/ProductsContext";
import { api, apiError, imgSrc } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const DeleteButton = ({ p, onDone }) => {
  const remove = async () => {
    try {
      await api.delete(`/admin/products/${p.slug}`);
      toast.success(`${p.name} deleted`);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={`admin-delete-${p.slug}`} className="grid h-10 w-10 place-items-center border border-black/15 hover:border-red-600 hover:text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this product?</AlertDialogTitle>
          <AlertDialogDescription>"{p.name}" will be removed from the catalogue. This can't be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="admin-delete-confirm" onClick={remove} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const Row = ({ p, i, onDeleted }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: p.slug });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} data-testid={`admin-row-${p.slug}`}
      className={`relative flex items-center gap-3 border-b border-black/15 bg-[#FAFAF7] py-4 sm:gap-4 ${isDragging ? "z-10 shadow-2xl ring-2 ring-[#FF5A00]" : ""}`}>
      <button ref={setActivatorNodeRef} {...attributes} {...listeners} data-testid={`admin-drag-${p.slug}`} aria-label={`Drag to reorder ${p.name}`}
        className="grid h-10 w-8 shrink-0 cursor-grab touch-none place-items-center text-zinc-500 hover:text-[#FF5A00] active:cursor-grabbing">
        <GripVertical className="h-5 w-5" />
      </button>
      <span className="hidden w-8 font-mono text-xs text-zinc-600 sm:block">{String(i + 1).padStart(2, "0")}</span>
      <div className="spec-frame h-16 w-16 shrink-0 overflow-hidden"><img src={imgSrc(p.image)} alt="" className="h-full w-full object-cover" /></div>
      <div className="min-w-0 flex-1">
        <p className="font-wide truncate text-base font-bold md:text-lg">{p.name}</p>
        <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-700">
          {p.category}{p.isNew && <span className="ml-2 bg-[#FFC400] px-1.5 py-0.5 text-[#0B0B0B]">New</span>}
        </p>
      </div>
      <Link to={`/admin/products/${p.slug}`} data-testid={`admin-edit-${p.slug}`} className="grid h-10 w-10 shrink-0 place-items-center border border-black/15 hover:border-[#FF5A00] hover:text-[#FF5A00]" aria-label="Edit"><Pencil className="h-4 w-4" /></Link>
      <DeleteButton p={p} onDone={onDeleted} />
    </div>
  );
};

export default function AdminDashboard() {
  const { products, refresh } = useProducts();
  const [items, setItems] = useState(products);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => { if (!dirty) setItems(products); }, [products, dirty]);

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setItems((list) => {
      const from = list.findIndex((x) => x.slug === active.id);
      const to = list.findIndex((x) => x.slug === over.id);
      return arrayMove(list, from, to);
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/products/reorder", { slugs: items.map((x) => x.slug) });
      await refresh();
      setDirty(false);
      toast.success("New order saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => { setItems(products); setDirty(false); };
  const onDeleted = () => { setDirty(false); refresh(); };
  const add = (
    <Link to="/admin/products/new" data-testid="admin-add-product" className="flex items-center gap-2 self-start bg-[#FF5A00] px-5 py-3.5 font-mono text-xs uppercase tracking-[0.15em] text-white hover:bg-[#0B0B0B] sm:self-auto">
      <Plus className="h-4 w-4" /> Add product
    </Link>
  );

  return (
    <AdminShell eyebrow={`${products.length} products in catalogue`} title="Products" actions={add}>
      <p className="mb-4 font-mono text-xs uppercase tracking-[0.15em] text-zinc-700" data-testid="admin-reorder-hint">Drag the <GripVertical className="inline h-4 w-4 align-[-3px]" /> handle to change the order products appear on the website.</p>
      {dirty && (
        <div data-testid="admin-reorder-bar" className="sticky top-20 z-30 mb-4 flex flex-col gap-3 bg-[#0B0B0B] p-4 text-white sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm">You changed the order. Save to update the website.</span>
          <div className="flex gap-2">
            <button onClick={reset} data-testid="admin-reorder-reset" className="border border-white/30 px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] hover:border-white">Undo</button>
            <button onClick={save} disabled={saving} data-testid="admin-reorder-save" className="flex items-center gap-2 bg-[#FF5A00] px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] hover:bg-[#FFC400] hover:text-[#0B0B0B] disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save order
            </button>
          </div>
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map((x) => x.slug)} strategy={verticalListSortingStrategy}>
          <div className="border-t border-black" data-testid="admin-product-list">
            {items.map((p, i) => <Row key={p.slug} p={p} i={i} onDeleted={onDeleted} />)}
          </div>
        </SortableContext>
      </DndContext>
    </AdminShell>
  );
}
